import { Component, Inject, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';

import {
    AnalyticsService,
    EnvironmentInterface,
    GuardApiInterface,
    LeadDTO,
    NetworkService,
    NotificationsService,
    ProUserService,
    RedirectCommandProvider,
    SetUpService
} from '@guardfinder/shared';

import { Observable, Subscription } from 'rxjs';

import { RedirectStrategies } from './services/redirect-strategy/infrastructure/redirect-strategies';

@Component({
    selector: 'gf-pro-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: true,
    imports: [IonApp, IonRouterOutlet]
})
export class AppComponent {
    userLoggedIn: boolean = false;

    onlineEvent: Observable<Event> | undefined;
    offlineEvent: Observable<Event> | undefined;

    subscriptions: Subscription[] = [];

    preselectedService: string | null = null;
    preselectedUserType: 'client' | 'lead' | null = null;
    preselectedUserInfo: LeadDTO | null = null;

    webappUrl: string = '';

    env!: EnvironmentInterface;

    constructor(
        @Inject('environment') environment: EnvironmentInterface,
        private platform: Platform,
        private router: Router,
        private nService: NotificationsService,
        private proUserService: ProUserService,
        private analytics: AnalyticsService,
        private networkService: NetworkService,
        private setUpService: SetUpService,
        private zone: NgZone,
        private readonly redirectCommandProvider: RedirectCommandProvider
    ) {
        this.webappUrl = environment.webappUrl.replace('https://', '');
        this.env = environment;

        this.initializeApp();
    }

    initUserData(user: GuardApiInterface | null) {
        if (!user) return;

        if (!this.env.isDev) return;

        this.nService.startNotificationSubscription();
    }

    async initPlatform() {
        if (this.platform.is('hybrid')) {
            this.networkService.capacitorNetworkWatchInit();
        } else {
            this.networkService.browserNetworkWatchInit();
        }
    }

    initGTMPageTracking() {
        this.router.events.forEach(item => {
            if (item instanceof NavigationEnd) {
                const gtmTag = {
                    event: 'page',
                    pageName: item.url
                };

                this.analytics.pushGTMTag(gtmTag);
            }
        });
    }

    async handleRedirects() {
        const isProLoggedIn = this.proUserService.isLoggedIn();
        const command = await this.redirectCommandProvider.execute(RedirectStrategies.APP_INIT, { isProLoggedIn });
        if (command.path) {
            await this.router.navigate([command.path], { queryParams: command.queryParams });
        }
    }

    initializeApp() {
        this.platform.ready().then(() => {
            this.initUserData(this.proUserService.user());

            this.initPlatform();

            this.initGTMPageTracking();

            this.setUpService.addScriptsToHead();
            this.networkService.checkConnection();

            if (this.platform.is('hybrid')) {
                App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
                    console.log('appurlopen');
                    this.zone.run(() => {
                        // Example url: https://beerswift.app/tabs/tab2
                        // slug = /tabs/tab2
                        const slug = event.url.split(this.webappUrl).pop();
                        if (slug) {
                            this.router.navigateByUrl(slug);
                        } else {
                            // If no match, do nothing - let regular routing
                            // logic take over
                            this.handleRedirects();
                        }
                    });
                });
            } else {
                this.handleRedirects();
            }

            SplashScreen.hide();
        });
    }
}
